import React, { useState, useCallback, useMemo } from 'react';
import { Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { WikiSpot } from '../../types';
import { wikiIcon } from './MapUtils';
import { getPointPosition } from './markerIconCache';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import L from 'leaflet';

/**
 * `wikiIcon(spot)` ignorerer `spot` helt (se MapUtils) — alle enkeltmarkører får
 * samme nål. Bygget ÉN gang: lagd inline i `.map()` ga et nytt L.divIcon per
 * render, og react-leaflet kaller `setIcon` når identiteten endres. Wiki-laget er
 * ett av dem som slår seg på av seg selv ved zoom ≥ 16, så dette traff nettopp når
 * man zoomer helt inn. Se markerIconCache.ts.
 */
const WIKI_ICON = wikiIcon(null);

interface ClusteredWikiMarkersProps {
  wikiSpots: WikiSpot[];
  onInteract: () => void;
  isMobile: boolean;
}

const ClusteredWikiMarkers: React.FC<ClusteredWikiMarkersProps> = ({ wikiSpots, onInteract }) => {
  const { t } = useTranslation();
  const [openSpotId, setOpenSpotId] = useState<number | null>(null);

  const closePopup = useCallback(() => setOpenSpotId(null), []);

  // Handler-objekt per plass. Klikket trenger `w.pageid`, så det kan ikke deles på
  // tvers slik som for ladere — men det kan bygges én gang per (liste, onInteract)
  // i stedet for per render. Uten dette fyrte @react-leaflet/core sin
  // `useEventHandlers` off()+on() på hver wiki-markør ved hver panorering.
  //
  // Hooks MÅ ligge før den tidlige returen under: en hook bak en betingelse endrer
  // antall hooks mellom rendringer så snart lista går fra tom til ikke-tom.
  const handlersByPageId = useMemo(() => {
    const m = new Map<number, { click: (e: L.LeafletMouseEvent) => void; popupclose: () => void }>();
    for (const w of wikiSpots) {
      m.set(w.pageid, {
        click: (e: L.LeafletMouseEvent) => {
          L.DomEvent.stop(e);
          setOpenSpotId(w.pageid);
          onInteract();
        },
        popupclose: closePopup,
      });
    }
    return m;
  }, [wikiSpots, onInteract, closePopup]);

  const openHandlers = useMemo(
    () => ({
      click: (e: L.LeafletMouseEvent) => { L.DomEvent.stop(e); onInteract(); },
      popupclose: closePopup,
    }),
    [onInteract, closePopup],
  );

  if (wikiSpots.length === 0) return null;

  const openSpot = openSpotId ? wikiSpots.find(w => w.pageid === openSpotId) : null;
  const spotsToCluster = openSpotId ? wikiSpots.filter(w => w.pageid !== openSpotId) : wikiSpots;

  return (
    <>
      {/* Open spot always rendered outside cluster */}
      {openSpot && (
        <Marker
          key={`wiki-open-${openSpot.pageid}`}
          position={getPointPosition(openSpot)}
          icon={WIKI_ICON}
          zIndexOffset={1000}
          eventHandlers={openHandlers}
        >
          <Popup className="custom-popup" minWidth={openSpot.thumbnail ? 280 : 200} maxWidth={320}>
            <WikiSpotPopup spot={openSpot} t={t} />
          </Popup>
        </Marker>
      )}

      <MarkerClusterGroup
        chunkedLoading
        maxClusterRadius={45}
        showCoverageOnHover={false}
        iconCreateFunction={(cluster: any) => wikiIcon(null, cluster.getChildCount())}
      >
        {spotsToCluster.map((w) => (
          <Marker
            key={`wiki-${w.pageid}`}
            position={getPointPosition(w)}
            icon={WIKI_ICON}
            zIndexOffset={70}
            eventHandlers={handlersByPageId.get(w.pageid)}
          >
            <Popup className="custom-popup" minWidth={w.thumbnail ? 280 : 200} maxWidth={320}>
              <WikiSpotPopup spot={w} t={t} />
            </Popup>
          </Marker>
        ))}
      </MarkerClusterGroup>
    </>
  );
};

const WikiSpotPopup: React.FC<{ spot: WikiSpot; t: any }> = ({ spot: w, t }) => (
  <div className="flex flex-col overflow-hidden rounded-t-xl -m-[1px] relative">
    {w.thumbnail ? (
      <div className="relative w-full h-40 bg-stone-100 group">
        <img src={w.thumbnail.source} className="w-full h-full object-cover" alt={w.title} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        <div className="absolute bottom-3 left-3 right-12">
          <h3 className="text-white font-black text-sm leading-tight drop-shadow-md">{w.title}</h3>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); window.open(w.url, '_blank'); }}
          className="absolute bottom-3 right-3 w-8 h-8 bg-white text-stone-900 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform z-10"
          title={t('map.wiki.read_article')}
        >
          <ExternalLink size={14} />
        </button>
      </div>
    ) : (
      <div className="p-4 pb-2">
        <div className="flex items-center gap-2 text-violet-600 font-black text-3xs uppercase tracking-widest mb-1">
          {t('map.wiki.attraction')}
        </div>
        <h3 className="text-stone-900 font-black text-sm leading-tight">{w.title}</h3>
      </div>
    )}
    {!w.thumbnail && (
      <div className="px-4 pb-4 pt-1 space-y-3">
        <div className="text-tiny text-[var(--text-muted)] line-clamp-4 leading-relaxed font-medium">
          {w.extract}
        </div>
        <button onClick={(e) => { e.stopPropagation(); window.open(w.url, '_blank'); }} className="w-full bg-violet-100 text-violet-700 hover:bg-violet-200 py-2 rounded-lg text-2xs font-black uppercase flex items-center justify-center gap-2 transition-colors">
          <ExternalLink size={12} /> {t('map.wiki.read_article')}
        </button>
      </div>
    )}
  </div>
);

export default ClusteredWikiMarkers;
